import { DropdownOption, stringArrayToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { OtpDto, TenantConfigDto } from 'ts-types/api.types';
import axios from 'axios';
import React, { Component } from 'react';
import moment from 'moment';
import { OneTimePasswordExpiration, OtpDuration } from 'ts-types/api.enums';
import { addTenantConfig, getOtpData, isOneTimePasswordExpired } from 'services/patientCentralServices';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { SemanticCOLORS } from 'semantic-ui-react/dist/commonjs/generic';
import FormContainer from 'components/layout/FormContainer';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import { Button, Grid, Icon, Message } from 'semantic-ui-react';
import DatePicker from 'components/final-form/DatePicker';
import { required, timeValidator } from 'utils/validatorUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import styled from 'styled-components';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';

const StyledRow = styled(Grid.Row)`
  border-bottom: 1px solid rgba(34, 36, 38, .15);
  margin-bottom: 0.5rem;
`;

const StyledContainer = styled.div`
  padding-bottom: 0.5rem;
`;

interface Props extends MessageSourceProps {
}

interface State {
  pageDataLoaded: boolean,
  otpData?: OtpDto,
  formValues: Partial<TenantConfigDto>,
  otpDurationOptions: DropdownOption[],
  otpExpiration: string,
  saveSuccess: boolean,
  errorMessage?: string,
}

class OneTimePasswordView extends Component<Props, State> {
  cancelTokenSource = axios.CancelToken.source();

  private initialValues: Partial<TenantConfigDto> = {};

  constructor(props: Props) {
    super(props);

    const {getMessage} = this.props;

    let currentTime = moment().format("HH:mm")
    let currentDate = moment().format("YYYY-MM-DD")

    const otpDurationOptions = stringArrayToDropdownOptionArray(
        Object.values(OtpDuration), getMessage, "enum.OtpDuration");

    this.initialValues = {
      creationDate: currentDate,
      creationTime: currentTime,
      otpDuration: "TWELVE_HOURS"
    };

    this.state = {
      pageDataLoaded: false,
      otpDurationOptions,
      otpExpiration: "EXPIRED",
      saveSuccess: false,
      formValues: {...this.initialValues}
    }

    this.fetchData();
  }

  fetchData = async () => {

    this.setState({
      pageDataLoaded: false
    })
    try {
      const otpData = await getOtpData(this.cancelTokenSource)
      const otpExpiration = await isOneTimePasswordExpired(this.cancelTokenSource)
      this.setState({
        otpData: otpData,
        otpExpiration: otpExpiration
      });

      this.initialValues.otpDuration = otpData.otpDuration;

    } catch (e) {
      this.handleError(e.response.data)
    } finally {
      this.setState({
        pageDataLoaded: true
      })
    }
  }

  handleSubmit = async (values: Partial<TenantConfigDto>) => {

    const {otpData} = this.state;
    let saveValues: Partial<TenantConfigDto> = {...values};

    const cancelTokenSource = this.cancelTokenSource;

    await addTenantConfig(saveValues, cancelTokenSource)
        .catch((e: any) => this.handleError(e.response.data))

    if(otpData) {
      this.setState({
        otpData: otpData,
        saveSuccess: true
      })
    }

    this.fetchData();

  };

  handleError(error: any) {
    const {getMessage} = this.props;
    this.setErrorMessage(getMessage('error.general'))
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    if (!this.state.pageDataLoaded) {
      return <></>;
    }

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={this.initialValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage} = this.props;
    const {otpDurationOptions, otpData, otpExpiration, saveSuccess, errorMessage} = this.state;

    const otpCreatedOn = otpData && otpData.otpCreateTime
        ? moment(otpData.otpCreateTime).format("DD.MM.YYYY HH:mm")
        : "";

    let otpExpiresOn = "";
    let otpExpirationDateTime = moment();
    if (otpData && otpData.otpDuration !== undefined && otpCreatedOn !== "") {
      if (otpData.otpDuration === OtpDuration.TWELVE_HOURS) {
        otpExpiresOn = moment(otpData.otpCreateTime).add(12, "hours").format("DD.MM.YYYY HH:mm")
        otpExpirationDateTime = moment(otpData.otpCreateTime).add(12, "hours");
      }
      if (otpData.otpDuration === OtpDuration.SIX_MONTHS) {
        otpExpiresOn = moment(otpData.otpCreateTime).add(6, "months").format("DD.MM.YYYY HH:mm")
        otpExpirationDateTime = moment(otpData.otpCreateTime).add(6, "months");
      }
    }

    const timeInputFormat = (value: any) => {
      if (moment(value, "H:mm").isValid()) {
        return moment(value, "H:mm").format('HH:mm');
      } else {
        return moment().format('HH:mm');
      }
    };

    const timeFormatOnFocus = (value: any) => {
      if (value === "Invalid date") {
        return "";
      }
    };

    const timeLeft = () => {

      let timeRemaining = "";
      const currentTime = moment();
      if (otpExpiration && otpExpiration !== OneTimePasswordExpiration.EXPIRED) {
        let months = otpExpirationDateTime.diff(currentTime, 'months')
        let days = otpExpirationDateTime.diff(currentTime, 'days')
        let hours = otpExpirationDateTime.diff(currentTime, 'hours')
        let minutes = otpExpirationDateTime.diff(currentTime, 'hours')

        let otpExpiration = moment(otpExpirationDateTime);
        if (months !== null && months > 0) {
          timeRemaining = months + "m ";
          otpExpiration = otpExpiration.subtract(months, 'months');
          days = otpExpiration.diff(currentTime, 'days')
        }
        if (days !== null && days > 0) {
          timeRemaining += days + "d ";
          otpExpiration = otpExpiration.subtract(days, 'days');
          hours = otpExpiration.diff(currentTime, 'hours')
        }
        if (hours !== null && hours > 0) {
          timeRemaining += hours + "h ";
          otpExpiration = otpExpiration.subtract(hours, 'hours');
          minutes = otpExpiration.diff(currentTime, 'minutes')
        }
        if (minutes !== null) {
          timeRemaining += minutes + "min ";
        }

        return getMessage("patientCentral.oneTimePassword.timeRemaining") + ": " + timeRemaining;
      }

      return getMessage("patientCentral.oneTimePassword.expired");
    }

    let otpIconColor: SemanticCOLORS = "red"
    if (otpExpiration === OneTimePasswordExpiration.VALID) {
      otpIconColor = "green";
    } else if (otpExpiration === OneTimePasswordExpiration.ALMOST_EXPIRED) {
      otpIconColor = "yellow";
    }

    return (
        <PraxStyledSegment>
          <form onSubmit={handleSubmit}>
            <FormContainer>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <PraxFormGrid>
                {
                  saveSuccess &&
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Message success onDismiss={() => this.setState({saveSuccess: false})}>
                        <p>
                          {getMessage("oneTimePassword.saveSuccess")}
                        </p>
                      </Message>
                    </Grid.Column>
                  </Grid.Row>
                }
                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("patientCentral.oneTimePassword.createdOn")} :
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <strong>{otpCreatedOn}</strong>
                  </Grid.Column>
                </Grid.Row>
                <StyledRow>
                  <Grid.Column width={4}>
                    <StyledContainer>
                      {getMessage("patientCentral.oneTimePassword.expires")} :
                    </StyledContainer>
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <StyledContainer>
                      <strong>{otpExpiresOn}</strong>
                    </StyledContainer>
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <StyledContainer>
                      <strong>{timeLeft()}</strong>
                      <Icon name={'circle'} color={otpIconColor}/>
                    </StyledContainer>
                  </Grid.Column>
                </StyledRow>
                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("patientCentral.oneTimePassword.otpCreateTime")} :
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                        fluid
                        name="creationDate"
                        component={DatePicker}
                        validate={required}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                        fluid
                        name="creationTime"
                        component={Input}
                        validate={timeValidator}
                        onFocus={(value: any) => timeFormatOnFocus(value)}
                        format={(value: any) => timeInputFormat(value)}
                        formatOnBlur
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("patientCentral.oneTimePassword.duration")} :
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                        id="otpDuration"
                        name="otpDuration"
                        component={Select}
                        options={otpDurationOptions}
                        validate={required}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4}>
                    {getMessage("patientCentral.oneTimePassword.password")} :
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Field
                        fluid
                        name="oneTimePassword"
                        component={TextArea}
                        rows={3}
                        validate={required}
                        autoFocus
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>

              <FormButtonsRow>
                <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              </FormButtonsRow>
            </FormContainer>
          </form>
        </PraxStyledSegment>
    );
  };

}

export default withMessages(OneTimePasswordView);
